import React, { useState } from 'react';
import RichTextEditor from './RichTextEditor';

function HomeEditor({ pageContent, setPageContent, uploading, uploadingField, handleImageUpload, handleRowImageUpload, deleteImageFromFirebase, addTableRow, deleteTableRow, updateTableRow }) {
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);

  const handleDeleteSection = (sectionName, itemId = null) => {
    if (window.confirm(`Are you sure you want to delete this ${sectionName}? This action cannot be undone.`)) {
      if (itemId) {
        deleteTableRow(sectionName, itemId);
      } else {
        setPageContent({ ...pageContent, [sectionName]: [] });
      }
    }
  };

  const handleDeleteAllSection = (sectionName) => {
    if (window.confirm(`Are you sure you want to delete ALL items in ${sectionName}? This action cannot be undone.`)) {
      setPageContent({ ...pageContent, [sectionName]: [] });
    }
  };

  return (
    <div className="page-editor">
      {/* Latest News Section with Rich Text */}
      <div className="editor-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h3>📰 Latest News Section</h3>
          <div>
            {pageContent.latestNews && pageContent.latestNews.length > 0 && (
              <button className="remove-btn" onClick={() => handleDeleteAllSection('latestNews')} style={{marginRight: 10}}>
                🗑️ Delete All News
              </button>
            )}
            <button className="add-btn" onClick={() => addTableRow('latestNews', { 
              title: '', 
              content: '', 
              date: new Date().toISOString().split('T')[0], 
              image: '',
              isBreaking: false,
              link: ''
            })}>
              + Add News
            </button>
          </div>
        </div>
        
        <div className="form-group">
          <label>Latest News Section Title</label>
          <input 
            value={pageContent.latestNewsTitle || 'Latest News'} 
            onChange={(e) => setPageContent({...pageContent, latestNewsTitle: e.target.value})} 
            className="form-input" 
            placeholder="e.g., Latest News & Updates"
          />
        </div>
        
        <div className="form-group">
          <label>Show Latest News Section?</label>
          <select 
            value={pageContent.showLatestNews || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showLatestNews: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        {(pageContent.latestNews || []).map((news) => (
          <div key={news.id} style={{border:'1px solid #e2e8f0', padding:15, marginBottom:15, borderRadius:8, position: 'relative'}}>
            <button 
              className="remove-btn" 
              onClick={() => handleDeleteSection('latestNews', news.id)}
              style={{position: 'absolute', top: 10, right: 10}}
            >
              🗑️ Delete News
            </button>
            
            <div className="form-group">
              <label>News Title</label>
              <input 
                placeholder="News Title" 
                value={news.title || ''} 
                onChange={(e) => updateTableRow('latestNews', news.id, 'title', e.target.value)} 
                className="form-input" 
              />
            </div>
            
            <div className="form-group">
              <label>News Content / Description (Rich Text)</label>
              <RichTextEditor 
                value={news.content || ''}
                onChange={(html) => updateTableRow('latestNews', news.id, 'content', html)}
                placeholder="Write the news content here..."
                imageFolder="latest-news-images"
              />
            </div>
            
            <div style={{display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap'}}>
              <div className="form-group" style={{flex: 1}}>
                <label>News Date</label>
                <input 
                  type="date" 
                  value={news.date || new Date().toISOString().split('T')[0]} 
                  onChange={(e) => updateTableRow('latestNews', news.id, 'date', e.target.value)} 
                  className="form-input" 
                />
              </div>
              
              <div className="form-group" style={{flex: 1}}>
                <label>Link URL (optional)</label>
                <input 
                  placeholder="https://..." 
                  value={news.link || ''} 
                  onChange={(e) => updateTableRow('latestNews', news.id, 'link', e.target.value)} 
                  className="form-input" 
                />
              </div>
            </div>
            
            <div className="form-group">
              <label style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <input 
                  type="checkbox" 
                  checked={!!news.isBreaking} 
                  onChange={(e) => updateTableRow('latestNews', news.id, 'isBreaking', e.target.checked)} 
                />
                Mark as Breaking News (highlighted in red)
              </label>
            </div>
            
            <div className="form-group">
              <label>News Image</label>
              {news.image && (
                <div style={{marginBottom: 10}}>
                  <img src={news.image} className="preview-img-sm" />
                </div>
              )}
              <div style={{display: 'flex', gap: 10}}>
                <label className="upload-label">
                  {uploading && uploadingField === `latestNews_${news.id}_image` ? '⏳...' : '📷 Upload Image'}
                  <input 
                    type="file" 
                    accept="image/*" 
                    style={{display:'none'}} 
                    onChange={(e) => { 
                      if (e.target.files[0]) handleRowImageUpload('latestNews', news.id, 'image', e.target.files[0]); 
                    }} 
                  />
                </label>
                {news.image && (
                  <button className="remove-btn" onClick={() => deleteImageFromFirebase(news.image, `latestNews_${news.id}_image`, false)}>
                    🗑️ Delete Image
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        
        <small style={{display:'block', marginTop:8, color:'#64748b'}}>
          Latest news will be displayed in reverse chronological order (newest first).
        </small>
      </div>

      {/* Achievements Section */}
      <div className="editor-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h3>🏆 Achievements Section</h3>
          {pageContent.achievements && pageContent.achievements.length > 0 && (
            <button className="remove-btn" onClick={() => handleDeleteAllSection('achievements')}>🗑️ Delete All Achievements</button>
          )}
        </div>
        <div className="form-group">
          <label>Show Achievements Section?</label>
          <select 
            value={pageContent.showAchievements || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showAchievements: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {(pageContent.achievements || []).map((a) => (
          <div key={a.id} style={{border:'1px solid #e2e8f0',padding:12,marginBottom:10, position: 'relative'}}>
            <button 
              className="remove-btn" 
              onClick={() => handleDeleteSection('achievements', a.id)}
              style={{position: 'absolute', top: 8, right: 8}}
            >
              🗑️ Delete
            </button>
            <input placeholder="Achievement Title" value={a.title || ''} onChange={(e) => updateTableRow('achievements', a.id, 'title', e.target.value)} className="form-input" style={{marginBottom: 8}} />
            <div className="form-group">
              <label>Description (Rich Text)</label>
              <RichTextEditor 
                value={a.description || ''}
                onChange={(html) => updateTableRow('achievements', a.id, 'description', html)}
                placeholder="Write achievement details..."
                imageFolder="achievement-images"
              />
            </div>
            <input placeholder="Year" value={a.year || ''} onChange={(e) => updateTableRow('achievements', a.id, 'year', e.target.value)} style={{width:100, marginBottom: 8}} className="form-input" />
            {a.image && <img src={a.image} className="preview-img-sm" />}
            <div style={{display: 'flex', gap: 10, marginTop: 8}}>
              <label className="upload-label">{uploading && uploadingField === `achievements_${a.id}_image` ? '⏳...' : '📷 Upload Image'}<input type="file" accept="image/*" style={{display:'none'}} onChange={(e) => { if (e.target.files[0]) handleRowImageUpload('achievements', a.id, 'image', e.target.files[0]); }} /></label>
              {a.image && <button className="remove-btn" onClick={() => deleteImageFromFirebase(a.image, `achievements_${a.id}_image`, false)}>🗑️ Delete Image</button>}
            </div>
          </div>
        ))}
        <button className="add-btn" onClick={() => addTableRow('achievements', { title: '', description: '', year: '', image: '' })}>+ Add Achievement</button>
      </div>

      {/* Notices Section with Rich Text */}
      <div className="editor-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h3>📢 Notices (News/Ticker)</h3>
          {pageContent.notices && pageContent.notices.length > 0 && (
            <button className="remove-btn" onClick={() => handleDeleteAllSection('notices')}>🗑️ Delete All Notices</button>
          )}
        </div>
        <div className="form-group">
          <label>Show Notices Section?</label>
          <select 
            value={pageContent.showNotices || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showNotices: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {(pageContent.notices || []).map((n) => (
          <div key={n.id} style={{border:'1px solid #e2e8f0',padding:12,marginBottom:10, position: 'relative'}}>
            <button 
              className="remove-btn" 
              onClick={() => handleDeleteSection('notices', n.id)}
              style={{position: 'absolute', top: 8, right: 8}}
            >
              🗑️ Delete Notice
            </button>
            <input placeholder="Notice Title" value={n.title || ''} onChange={(e) => updateTableRow('notices', n.id, 'title', e.target.value)} className="form-input" style={{marginBottom: 8}} />
            <div className="form-group">
              <label>Notice Content (Rich Text)</label>
              <RichTextEditor 
                value={n.content || ''}
                onChange={(html) => updateTableRow('notices', n.id, 'content', html)}
                placeholder="Write notice content here..."
                imageFolder="notice-images"
              />
            </div>
            <input type="date" value={n.date || ''} onChange={(e) => updateTableRow('notices', n.id, 'date', e.target.value)} className="form-input" style={{marginBottom: 8}} />
            <div className="form-group">
              <label>Notice Banner Image (optional)</label>
              {n.image && <img src={n.image} className="preview-img-sm" />}
              <div style={{display: 'flex', gap: 10, marginTop: 8}}>
                <label className="upload-label">{uploading && uploadingField === `notices_${n.id}_image` ? '⏳...' : '📷 Upload Banner Image'}<input type="file" accept="image/*" style={{display:'none'}} onChange={(e) => { if (e.target.files[0]) handleRowImageUpload('notices', n.id, 'image', e.target.files[0]); }} /></label>
                {n.image && <button className="remove-btn" onClick={() => deleteImageFromFirebase(n.image, `notices_${n.id}_image`, false)}>🗑️ Delete Image</button>}
              </div>
            </div>
            <label style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 8}}>
              <input type="checkbox" checked={!!n.isOld} onChange={(e) => updateTableRow('notices', n.id, 'isOld', e.target.checked)} /> Archive
            </label>
          </div>
        ))}
        <button className="add-btn" onClick={() => addTableRow('notices', { title: '', content: '', date: new Date().toISOString().split('T')[0], isOld: false, image: '' })}>+ Add Notice</button>
      </div>

      {/* Testimonials Section */}
      <div className="editor-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h3>💬 Testimonials</h3>
          {pageContent.testimonials && pageContent.testimonials.length > 0 && (
            <button className="remove-btn" onClick={() => handleDeleteAllSection('testimonials')}>🗑️ Delete All Testimonials</button>
          )}
        </div>
        <div className="form-group">
          <label>Show Testimonials Section?</label>
          <select 
            value={pageContent.showTestimonials || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showTestimonials: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {(pageContent.testimonials || []).map((t) => (
          <div key={t.id} style={{border:'1px solid #e2e8f0',padding:12,marginBottom:10, position: 'relative'}}>
            <button 
              className="remove-btn" 
              onClick={() => handleDeleteSection('testimonials', t.id)}
              style={{position: 'absolute', top: 8, right: 8}}
            >
              🗑️ Delete
            </button>
            <div className="form-group">
              <label>Testimonial (Rich Text)</label>
              <RichTextEditor 
                value={t.text || ''}
                onChange={(html) => updateTableRow('testimonials', t.id, 'text', html)}
                placeholder="Write testimonial here..."
                imageFolder="testimonial-images"
              />
            </div>
            <input placeholder="Name" value={t.name || ''} onChange={(e) => updateTableRow('testimonials', t.id, 'name', e.target.value)} className="form-input" style={{marginBottom: 8}} />
            <input placeholder="Designation" value={t.designation || ''} onChange={(e) => updateTableRow('testimonials', t.id, 'designation', e.target.value)} className="form-input" style={{marginBottom: 8}} />
            {t.image && <img src={t.image} className="preview-img-sm" />}
            <div style={{display: 'flex', gap: 10, marginTop: 8}}>
              <label className="upload-label">{uploading && uploadingField === `testimonials_${t.id}_image` ? '⏳...' : '📷 Upload Photo'}<input type="file" accept="image/*" style={{display:'none'}} onChange={(e) => { if (e.target.files[0]) handleRowImageUpload('testimonials', t.id, 'image', e.target.files[0]); }} /></label>
              {t.image && <button className="remove-btn" onClick={() => deleteImageFromFirebase(t.image, `testimonials_${t.id}_image`, false)}>🗑️ Delete Image</button>}
            </div>
          </div>
        ))}
        <button className="add-btn" onClick={() => addTableRow('testimonials', { text: '', name: '', designation: '' })}>+ Add Testimonial</button>
      </div>

      {/* Collaborations & Associations Section */}
      <div className="editor-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h3>🤝 Collaborations & Associations</h3>
          {pageContent.collaborations && pageContent.collaborations.length > 0 && (
            <button className="remove-btn" onClick={() => handleDeleteAllSection('collaborations')}>🗑️ Delete All</button>
          )}
        </div>
        <div className="form-group">
          <label>Show Collaborations Section?</label>
          <select 
            value={pageContent.showCollaborations || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showCollaborations: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {(pageContent.collaborations || []).map((c) => (
          <div key={c.id} style={{display:'flex', gap:8, marginBottom:8, alignItems:'center', flexWrap: 'wrap', position: 'relative', padding: 8, border: '1px solid #e2e8f0', borderRadius: 8}}>
            {c.logo && <img src={c.logo} style={{width:50,height:50, objectFit:'contain'}} alt={c.name} />}
            <label className="upload-label">{uploading && uploadingField === `collaborations_${c.id}_logo` ? '⏳...' : '📷 Upload Logo'}<input type="file" accept="image/*" style={{display:'none'}} onChange={(e) => { if (e.target.files[0]) handleRowImageUpload('collaborations', c.id, 'logo', e.target.files[0]); }} /></label>
            <input placeholder="Organization Name" value={c.name || ''} onChange={(e) => updateTableRow('collaborations', c.id, 'name', e.target.value)} className="form-input" style={{flex:1, minWidth: '150px'}} />
            <input placeholder="Website URL (optional)" value={c.website || ''} onChange={(e) => updateTableRow('collaborations', c.id, 'website', e.target.value)} className="form-input" style={{flex:1, minWidth: '150px'}} />
            <button className="remove-btn" onClick={() => handleDeleteSection('collaborations', c.id)}>🗑️ Delete</button>
            {c.logo && <button className="remove-btn" onClick={() => deleteImageFromFirebase(c.logo, `collaborations_${c.id}_logo`, false)}>🗑️ Delete Logo</button>}
          </div>
        ))}
        <button className="add-btn" onClick={() => addTableRow('collaborations', { name: '', logo: '', website: '' })}>+ Add Collaboration/Association</button>
      </div>

      {/* Upcoming Events Section */}
      <div className="editor-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h3>📅 Upcoming Events Calendar</h3>
          {pageContent.upcomingEvents && pageContent.upcomingEvents.length > 0 && (
            <button className="remove-btn" onClick={() => handleDeleteAllSection('upcomingEvents')}>🗑️ Delete All Events</button>
          )}
        </div>
        <div className="form-group">
          <label>Show Upcoming Events Section?</label>
          <select 
            value={pageContent.showUpcomingEvents || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showUpcomingEvents: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div className="form-group">
          <label>Calendar Title</label>
          <input value={pageContent.calendarTitle || 'Upcoming Events'} onChange={(e) => setPageContent({...pageContent, calendarTitle: e.target.value})} className="form-input" />
        </div>
        <div className="form-group">
          <label>Event Items (Will be displayed in chronological order)</label>
          {(pageContent.upcomingEvents || []).map((event) => (
            <div key={event.id} style={{border:'1px solid #e2e8f0',padding:12,marginBottom:10, position: 'relative'}}>
              <button 
                className="remove-btn" 
                onClick={() => handleDeleteSection('upcomingEvents', event.id)}
                style={{position: 'absolute', top: 8, right: 8}}
              >
                🗑️ Delete Event
              </button>
              <div style={{display:'flex', gap:8, marginBottom:8}}>
                <input type="date" value={event.date || ''} onChange={(e) => updateTableRow('upcomingEvents', event.id, 'date', e.target.value)} className="form-input" style={{width:'auto'}} />
                <input placeholder="Event Title" value={event.title || ''} onChange={(e) => updateTableRow('upcomingEvents', event.id, 'title', e.target.value)} className="form-input" style={{flex:2}} />
              </div>
              <div className="form-group">
                <label>Event Description (Rich Text)</label>
                <RichTextEditor 
                  value={event.description || ''}
                  onChange={(html) => updateTableRow('upcomingEvents', event.id, 'description', html)}
                  placeholder="Event description..."
                  imageFolder="event-images"
                />
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <input placeholder="Event Time (e.g., 10:00 AM)" value={event.time || ''} onChange={(e) => updateTableRow('upcomingEvents', event.id, 'time', e.target.value)} className="form-input" style={{width:150}} />
                <input placeholder="Venue/Location" value={event.venue || ''} onChange={(e) => updateTableRow('upcomingEvents', event.id, 'venue', e.target.value)} className="form-input" style={{flex:1}} />
              </div>
            </div>
          ))}
          <button className="add-btn" onClick={() => addTableRow('upcomingEvents', { date: new Date().toISOString().split('T')[0], title: '', description: '', time: '', venue: '' })}>+ Add Event</button>
        </div>
        <small style={{display:'block', marginTop:8, color:'#64748b'}}>Events will be displayed in chronological order. Past events will be shown in grey.</small>
      </div>

      {/* CTA Section */}
      <div className="editor-card">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
          <h3>🎯 Call to Action (CTA) Section</h3>
          {pageContent.showCTA !== 'no' && pageContent.ctaTitle && (
            <button className="remove-btn" onClick={() => {
              if (window.confirm('Are you sure you want to hide/clear the CTA section?')) {
                setPageContent({...pageContent, showCTA: 'no', ctaTitle: '', ctaSubtitle: '', ctaButtonText: '', ctaButtonLink: '', ctaBackgroundImage: ''});
              }
            }}>🗑️ Remove CTA Section</button>
          )}
        </div>
        <div className="form-group">
          <label>Show CTA?</label>
          <select value={pageContent.showCTA || 'yes'} onChange={(e) => setPageContent({...pageContent, showCTA: e.target.value})} className="form-input">
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {pageContent.showCTA !== 'no' && (
          <>
            <div className="form-group">
              <label>CTA Title</label>
              <input value={pageContent.ctaTitle || ''} onChange={(e) => setPageContent({...pageContent, ctaTitle: e.target.value})} className="form-input" />
            </div>
            <div className="form-group">
              <label>CTA Subtitle (Rich Text)</label>
              <RichTextEditor 
                value={pageContent.ctaSubtitle || ''}
                onChange={(html) => setPageContent({...pageContent, ctaSubtitle: html})}
                placeholder="CTA subtitle..."
                imageFolder="cta-images"
              />
            </div>
            <div className="form-group">
              <label>Button Text</label>
              <input value={pageContent.ctaButtonText || 'Apply Now'} onChange={(e) => setPageContent({...pageContent, ctaButtonText: e.target.value})} className="form-input" />
            </div>
            <div className="form-group">
              <label>Button Link</label>
              <input value={pageContent.ctaButtonLink || '/admissions'} onChange={(e) => setPageContent({...pageContent, ctaButtonLink: e.target.value})} className="form-input" />
            </div>
            <div className="form-group">
              <label>CTA Background Image (optional)</label>
              {pageContent.ctaBackgroundImage && <img src={pageContent.ctaBackgroundImage} className="preview-img" />}
              <div style={{display: 'flex', gap: 10, marginTop: 10}}>
                <label className="upload-label">{uploading && uploadingField === 'ctaBackgroundImage' ? '⏳...' : '📷 Upload Background'}<input type="file" accept="image/*" style={{display:'none'}} onChange={(e) => { if (e.target.files[0]) handleImageUpload('ctaBackgroundImage', e.target.files[0]); }} /></label>
                {pageContent.ctaBackgroundImage && <button className="remove-btn" onClick={() => deleteImageFromFirebase(pageContent.ctaBackgroundImage, 'ctaBackgroundImage', false)}>🗑️ Delete Image</button>}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .page-editor {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .editor-card {
          background: #fff;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .editor-card h3 {
          margin: 0 0 8px;
          color: #1a3a6b;
        }
        
        .form-group {
          margin-bottom: 20px;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #334155;
        }
        
        .form-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 14px;
        }
        
        .add-btn {
          background: #10b981;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }
        
        .remove-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
        }
        
        .upload-label {
          background: #3b82f6;
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          display: inline-block;
        }
        
        .preview-img-sm {
          width: 80px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
}

export default HomeEditor;